import { useEffect, useRef, useState } from 'react';

/**
 * Observes an element and reports whether it is inside the viewport, so
 * sections can animate in on scroll. Falls back to "in view" when
 * IntersectionObserver is unavailable.
 * @param {boolean} [once] - stop observing after the first intersection
 * @returns {{ ref: { current: HTMLElement | null }, inView: boolean }}
 */
export function useInView(once = true) {
	const ref = useRef(/** @type {HTMLElement | null} */ (null));
	const [inView, setInView] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el || typeof IntersectionObserver === 'undefined') {
			setInView(true);
			return undefined;
		}
		const observer = new IntersectionObserver((entries) => {
			for (const entry of entries) {
				if (entry.isIntersecting) {
					setInView(true);
					if (once) { observer.disconnect(); }
				} else if (!once) {
					setInView(false);
				}
			}
		}, { threshold: 0.2 });
		observer.observe(el);
		return () => observer.disconnect();
	}, [once]);

	return { ref, inView };
}
